import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, Truck, Clock, Navigation, Phone, RefreshCw, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo
const LOGO_URL = "https://i.ibb.co/7xkFLsM4/tempus-logo-white-blue.png";

export default function TrackingPage() {
  const { token } = useParams();
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const mapRef = useRef(null);

  const fetchTrackingData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tracking/${token}`);
      if (!response.ok) {
        throw new Error('Tracking not found');
      }
      const data = await response.json();
      setTrackingData(data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrackingData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchTrackingData, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getGoogleMapsUrl = () => {
    if (!trackingData?.current_location) return null;
    const { latitude, longitude } = trackingData.current_location;
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
  };

  const getDirectionsUrl = () => {
    if (!trackingData?.current_location || !trackingData?.destination) return null;
    const { latitude, longitude } = trackingData.current_location;
    const dest = trackingData.destination;
    return `https://www.google.com/maps/dir/${latitude},${longitude}/${dest.city},${dest.state}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Tracking Not Found</h2>
          <p className="text-gray-400">This tracking link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Tempus Dispatch" className="h-8" />
          </div>
          <button
            onClick={fetchTrackingData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Banner */}
        <div className={`rounded-xl p-6 mb-6 ${
          trackingData?.status === 'active' 
            ? 'bg-green-500/10 border border-green-500/30' 
            : 'bg-yellow-500/10 border border-yellow-500/30'
        }`}>
          <div className="flex items-center gap-4">
            <div className={`w-4 h-4 rounded-full ${
              trackingData?.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'
            }`}></div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {trackingData?.status === 'active' ? '🚛 Live Tracking Active' : '⏸️ Tracking Paused'}
              </h1>
              <p className="text-gray-400">
                Load #{trackingData?.load_id?.slice(0, 8).toUpperCase()}
              </p>
            </div>
          </div>
        </div>

        {/* Driver Info */}
        {trackingData?.driver_name && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center">
                <Truck className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">{trackingData.driver_name}</h3>
                <p className="text-gray-400">Driver</p>
              </div>
            </div>
          </div>
        )}

        {/* Location & ETA Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Current Location */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-6 h-6 text-blue-400" />
              <h3 className="text-lg font-semibold text-white">Current Location</h3>
            </div>
            {trackingData?.current_location ? (
              <div>
                <p className="text-gray-300 mb-2">
                  {trackingData.current_location.latitude.toFixed(4)}, {trackingData.current_location.longitude.toFixed(4)}
                </p>
                {trackingData.current_location.speed && (
                  <p className="text-sm text-gray-400">
                    Speed: {Math.round(trackingData.current_location.speed)} mph
                  </p>
                )}
                <a
                  href={getGoogleMapsUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                >
                  <Navigation className="w-4 h-4" />
                  View on Google Maps
                </a>
              </div>
            ) : (
              <p className="text-gray-400">Waiting for driver location...</p>
            )}
          </div>

          {/* ETA */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-6 h-6 text-green-400" />
              <h3 className="text-lg font-semibold text-white">Estimated Arrival</h3>
            </div>
            {trackingData?.eta ? (
              <div>
                <p className="text-3xl font-bold text-green-400 mb-1">
                  {trackingData.eta.eta_hours < 1 
                    ? `${Math.round(trackingData.eta.eta_hours * 60)} min`
                    : `${trackingData.eta.eta_hours.toFixed(1)} hrs`
                  }
                </p>
                <p className="text-gray-400 mb-2">
                  {trackingData.eta.distance_miles} miles remaining
                </p>
                <p className="text-sm text-gray-500">
                  ETA: {new Date(trackingData.eta.eta_time).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
              </div>
            ) : (
              <p className="text-gray-400">Calculating...</p>
            )}
          </div>
        </div>

        {/* Route Info */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">Route Details</h3>
          <div className="flex items-center gap-4">
            {/* Origin */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-400">PICKUP</span>
              </div>
              <p className="text-white font-medium">
                {trackingData?.origin?.city || 'N/A'}, {trackingData?.origin?.state || ''}
              </p>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0">
              <div className="w-16 h-0.5 bg-gradient-to-r from-green-500 to-red-500"></div>
            </div>

            {/* Destination */}
            <div className="flex-1 text-right">
              <div className="flex items-center justify-end gap-2 mb-1">
                <span className="text-sm text-gray-400">DELIVERY</span>
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              </div>
              <p className="text-white font-medium">
                {trackingData?.destination?.city || 'N/A'}, {trackingData?.destination?.state || ''}
              </p>
            </div>
          </div>

          {getDirectionsUrl() && (
            <a
              href={getDirectionsUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors"
            >
              <Navigation className="w-4 h-4" />
              Get Directions
            </a>
          )}
        </div>

        {/* Last Update */}
        <div className="text-center text-gray-500 text-sm">
          Last updated: {lastUpdate?.toLocaleTimeString() || 'N/A'}
          <br />
          Auto-refreshes every 30 seconds
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-auto py-6 text-center text-gray-500 text-sm">
        <p>Powered by <span className="text-blue-400">Tempus Dispatch</span></p>
        <p className="mt-1">© 2026 Tempus Dispatch LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}
